import { Component, OnInit, OnDestroy } from "@angular/core";
import { Router } from "@angular/router";
import { forkJoin, Subscription, from } from "rxjs";
import { tap, switchMap } from "rxjs/operators";
//import { GridOptions, ColDef, AllModules, ISetFilterParams } from '@ag-grid-enterprise/all-modules';
import { GridOptions, ColDef, ISetFilterParams, GridApi } from "ag-grid-community";

import { IconActionRendererComponent } from "../../../common/components/ag-grid/icon-action-renderer.component";
import { CheckboxRendererComponent } from "../../../common/components/ag-grid/checkbox-renderer.component";
import { AuthService } from "../../../services/auth.service";
import { ContactService } from "../../services/contacts.service";

import { DynamicDialogService } from "../../../core/services/dynamic-dialog.service";
import { Dialog, DialogButton } from "../../../core/models/dialog.model";
import { LoaderService } from './../../../common/services/loader.service';
import { OrganizationService } from "../../../common/services/organization.service";
import { SubHeaderService } from "../../../core/services/sub-header.service";

import { AgentTypeOptions, Contact, AgentType } from "../../models/contact.model";
import { Department } from './../../../common/models/organization.model';
import { Option } from "../../../common/models/flow.dto";
import { SubHeader } from "../../../core/models/sub-header.model";
import { LunaClaimTypes, LunaClaimValues } from "../../../models/user-identity.model";
import { GridFormatterService } from "../../../common/services/grid-formatter.service";
import { AgGridBooleanFilterComponent, IBooleanFilterParams } from "../../../common/components/ag-grid/filters/boolean-filter.component";

@Component({
    selector: "manage-contacts",
    templateUrl: "./manage-contacts.component.html",
    standalone: false
})
export class ManageContactsComponent implements OnInit, OnDestroy {

    public agentTypes = new Array<Option>();
    public contacts: Array<Contact>;
    public departments: Department[];
    public isInternalOrg: boolean = false;
    public subHeader: SubHeader = new SubHeader({ title: "Manage Contacts" });
    public objectId: string;
    public orgId: string;
	public gridOptions: GridOptions;
	public gridApi?: GridApi;
    organizationType: string = "";
    _booleanFilterParams = { isInServerSideFilterMode: false } as IBooleanFilterParams;
    _isEnabledBooleanFilterParams = { isInServerSideFilterMode: false } as IBooleanFilterParams;
    hasManageContactsPermission = false;
    hasManageContactsLimitedPermission = false;
    hasEitherManageContactPermissions = false;
    hasEditContactsPermission = false;
    hasManageOwnContactPermission = false;

    confirmDialog: Dialog = new Dialog("Are you sure you want to delete UserName?", [new DialogButton("Confirm", true, () => this.onDeleteConfirm()), new DialogButton("Cancel", false, () => this.onDialogClose())])
    readonly confirmDialogMessage: string = "Are you sure you want to delete UserName?";
    managedDeleteDialog: Dialog = new Dialog("Each Entity must have a AgentType.  You must add a new contact or modify an existing agent with a different authority type to be AgentType before deleting UserName as AgentType.  <br /> <br />Please use the Edit Contact option on the Manage Contacts page to update an existing contact's Agent Type Authority.  Otherwise, please use Add New Contact.", [new DialogButton("OK", false, () => { return; })]);
    readonly managedDeleteDialogMessage : string = "Each Entity must have a AgentType.  You must add a new contact or modify an existing agent with a different authority type to be AgentType before deleting UserName as AgentType.  <br /> <br />Please use the Edit Contact option on the Manage Contacts page to update an existing contact's Agent Type Authority.  Otherwise, please use Add New Contact.";

    private fullActionColumn: ColDef = {
        headerName: "",
        field: "",
        cellStyle: { 'text-align': 'right', 'line-height': 1.75 },
        cellRenderer: IconActionRendererComponent,
        cellRendererParams: {
            buttons: [
                {
                    action: (obj: any) => this.edit(obj),
                    label: 'Edit Contact',
                    icon: 'edit',
                    isVisible: (obj: any) => this.canEditContact(obj)
                },
                {
                    action: (obj: any) => this.view(obj),
                    label: 'View Contact',
                    icon: 'view'
                },
                {
                    action: (obj: any) => this.delete(obj),
                    label: 'Delete Contact',
                    icon: 'delete',
                    isVisible: (obj: any) => this.canDeleteContact(obj)
                }
            ]
        },
        maxWidth: 100
    };

    private actionColumnNoEdit: ColDef = {
        headerName: "",
        field: "",
        cellStyle: { 'text-align': 'right', 'line-height': 1.75 },
        cellRenderer: IconActionRendererComponent,
        cellRendererParams: {
            buttons: [
                {
                    action: (obj: any) => this.view(obj),
                    label: 'View Contact',
                    icon: 'view'
                },
                {
                    action: (obj: any) => this.delete(obj),
                    label: 'Delete Contact',
                    icon: 'delete',
                    isVisible: (obj: any) => this.canDeleteContact(obj)
                }
            ]
        },
        maxWidth: 100
    }

    dataSubscriptions: Subscription = new Subscription();

    constructor(
        public authService: AuthService,
        public contactService: ContactService,
        public loader: LoaderService,
        public subHeaderService: SubHeaderService,
        public router: Router,
        public dialogService: DynamicDialogService,
        public orgService: OrganizationService,
        public gridFormatterService: GridFormatterService,
    ) {
    }


    ngOnInit(): void {
        this.loader.toggleLoader(false);

        //If the user has the ability to manage contacts, add the button to the subheader
        this.hasManageContactsPermission = this.authService.User.allClaims.filter(a => a.type == LunaClaimTypes.System && a.value == LunaClaimValues.ManageContacts).length > 0;
        this.hasManageContactsLimitedPermission = this.authService.User.allClaims.filter(a => a.type == LunaClaimTypes.System && a.value == LunaClaimValues.ManageContactsLimited).length > 0;
        this.hasEitherManageContactPermissions = this.hasManageContactsPermission || this.hasManageContactsLimitedPermission;
        this.hasEditContactsPermission = this.authService.User.allClaims.filter(a => a.type == LunaClaimTypes.System && a.value == LunaClaimValues.EditContact).length > 0;
        this.hasManageOwnContactPermission = this.authService.User.allClaims.filter(a => a.type == LunaClaimTypes.System && a.value == LunaClaimValues.ManageOwnContact).length > 0;
        if (this.hasEitherManageContactPermissions && this.hasEditContactsPermission) {
            this.subHeader.buttons = [{ name: "Add New Contact", method: () => this.addNewContact() }];

            if (!this.authService.User.isInternal) this.subHeader.buttons.push({ name: "Edit Contact", method: () => this.startEditFlow()});
        }

        this.subHeaderService.updateSubHeader(this.subHeader);
        this.agentTypes = AgentTypeOptions.All;

        this.orgId = this.authService.User.organizationId;

        const contactObs = this.contactService.getAll<Contact[]>({ stubs: true, organizationId: this.orgId  });
        const orgObs = this.orgService.getOrganizationStub(this.orgId);

        //Default the defs to not have departments
        this.gridOptions = this.gridFormatterService.BuildDefaultGridOptionsWithColumns(this.createColumnDefs(false));
		this.gridOptions.onGridReady = (params) => {
			this.gridApi = params.api;
		};
		this.dataSubscriptions.add(forkJoin([contactObs, orgObs]).pipe(switchMap(([contactList, orgData]) =>
		{
            this.contacts = contactList;
            this.departments = orgData.departments;
            this.isInternalOrg = orgData.isInternal;
            this.organizationType = orgData.entityType;
            this.gridApi.setGridOption("columnDefs", []);
            this.gridApi.setGridOption("columnDefs", this.createColumnDefs(this.isInternalOrg));
			this.gridApi.sizeColumnsToFit();
			
			return from(this.gridApi.setColumnFilterModel('canLogIn', { type: AgGridBooleanFilterComponent, filter: true }))
				.pipe(tap(() => {
					this.gridApi.onFilterChanged();
				}));
        })).subscribe());
    }

    ngOnDestroy() {
        this.dataSubscriptions.unsubscribe();
    }

    public addNewContact(): void {
        //Internal users get the previously existing add contact process
        if (this.authService.User.isInternal)
            this.router.navigate(['admin', 'contact', 'add', this.authService.User.isInternal, this.authService.User.organizationId]);
        //External users get routed to use the add contact flow.
        else
            this.router.navigate(['flow', 'create', 'oxuNg1LoxvMUSQAwU']);
    }

    public canAddContact() {
        let isExternal = !this.authService.User.isInternal;
    }

    public edit(obj: Contact): void {
        this.router.navigate([`/admin/contact/${obj.id}`])
    }

    public view(obj: Contact): void {
        this.router.navigate([`/admin/contact/view/${obj.id}`])
    }

    public delete(obj: Contact): void {
        this.objectId = obj.id;
        this.selectDeletionDialog(obj);
    }

    public departmentNameFormatter(component): any {
        return (obj: any) => {
            if (!obj.data.departmentId)
                return 'Not found';
            const dept = (component.departments as Department[]).find(d => d.id == obj.data.departmentId);
            if (dept)
                return dept.displayName;
            return "Unassigned";
        };
    }

    public createColumnDefs(isInternalOrg: boolean): ColDef[] {
        const defaultColumns: ColDef[] = [
            {
                headerName: "Contact Name",
                headerTooltip: "Contact Name",
                field: "displayName",
                tooltipField: "displayName",
                filter: "agTextColumnFilter",
                filterParams: this.gridFormatterService.defaultTextFilterParams,
            },
            {
                headerName: "Is User",
                headerTooltip: "Is User",
                field: "isUser",
                tooltipValueGetter: this.gridFormatterService.formatBooleanToYesNo("isUser"),
                cellRenderer: CheckboxRendererComponent,
                cellRendererParams: {
                    action: () => { return false; },
                    field: "isUser",
                    description: "Is User",
                    isVisible: () => { return true; },
                    isEnabled: () => { return false; },
                    renderAsYesNo: true
                },
                filter: AgGridBooleanFilterComponent,
                filterParams: this._booleanFilterParams,
                maxWidth: 110
            },
            {
                headerName: "Is Enabled",
                headerTooltip: "Is Enabled",
                field: "canLogIn",
                tooltipValueGetter: this.gridFormatterService.formatBooleanToYesNo("canLogIn"),
                cellRenderer: CheckboxRendererComponent,
                cellRendererParams: {
                    action: () => { return false; },
                    field: "canLogIn",
                    description: "Is Enabled",
                    isVisible: () => { return true; },
                    isEnabled: () => { return false; },
                    renderAsYesNo: true
                },
				filter: AgGridBooleanFilterComponent,
                filterParams: this._isEnabledBooleanFilterParams,
                colId: "canLogIn",
                sortingOrder: ["desc", "asc", null],
                sort: "desc",
                maxWidth: 145
            }
        ];
        //Only show departments if this is an internal org
        if (isInternalOrg) {
            defaultColumns.push({
                headerName: "Department",
                headerTooltip: "Department",
                field: "departmentId",
                tooltipField: "departmentId",
                valueGetter: this.departmentNameFormatter(this)
            });
        }
        //Else Show agent types
        else {
            defaultColumns.push({
                headerName: 'Agent Type',
                headerTooltip: 'Agent Type',
                field: 'agentTypes',
                valueGetter: this.typeFormatter(this),
                tooltipValueGetter: this.typeFormatter(this),
            })
        }

        //once the Deactivate or Change Authority flow is ready, uncomment the conditional columns below and remove the button column following the commented section
        //if (this.authService.User.isInternal) {
        //    defaultColumns.push(this.fullActionColumn);
        //} else {
        //    defaultColumns.push(this.actionColumnNoEdit);
        //}
        defaultColumns.push({
            headerName: "",
            field: "",
            cellStyle: { 'text-align': 'right', 'line-height': 1.75 },
            cellRenderer: IconActionRendererComponent,
            cellRendererParams: {
                buttons: [
                    {
                        action: (obj: any) => this.edit(obj),
                        label: 'Edit Contact',
                        icon: 'edit',
                        isVisible: (obj: any) => this.canEditContact(obj)
                    },
                    {
                        action: (obj: any) => this.view(obj),
                        label: 'View Contact',
                        icon: 'view'
                    },
                    {
                        action: (obj: any) => this.delete(obj),
                        label: 'Delete Contact',
                        icon: 'delete',
                        isVisible: (obj: any) => this.canDeleteContact(obj)
                    }
                ]
            },
            maxWidth: 100
        });
        return defaultColumns;
    }

    isDesignatedRep(agentType: AgentType): boolean { return (agentType & AgentType.DesignatedRep) == AgentType.DesignatedRep; }
    isEmergencyPOC(agentType: AgentType): boolean { return (agentType & AgentType.EmergencyPOC) == AgentType.EmergencyPOC; }
    isStatutoryAgent(agentType: AgentType): boolean { return (agentType & AgentType.StatutoryAgent) == AgentType.StatutoryAgent; }
    isExecutiveAuthority(agentType: AgentType): boolean { return (agentType & AgentType.ExecutiveAuthority) == AgentType.ExecutiveAuthority; }
    isPrimaryContact(agentType: AgentType): boolean { return (agentType & AgentType.PrimaryContact) == AgentType.PrimaryContact; }

    canEditContact(obj: Contact) {
        let isCurrentUser = this.authService.User.id === obj.id;
        let isInternalUserLookingAtInternalOrg = this.authService.User.isInternal && this.isInternalOrg;
        return isCurrentUser || isInternalUserLookingAtInternalOrg;
    }

    canDeleteContact(obj: Contact) {
        let hasOneOfValidLimitedAgentTypes = this.isDesignatedRep(obj.agentType) || this.isEmergencyPOC(obj.agentType) || this.isStatutoryAgent(obj.agentType);
        let isCurrentUser = this.authService.User.id === obj.id;

        //Can delete/disable it if permissions are present, if the selected contact is different from current user, and if that user isn't already deleted/disabled
        if ((this.hasManageContactsPermission || (this.hasManageContactsLimitedPermission && hasOneOfValidLimitedAgentTypes)) && !isCurrentUser && obj.canLogIn
                && this.hasEditContactsPermission)
            return true;
        else return false;
    }

    onDeleteConfirm(): void {
        this.loader.toggleLoader(false);
        this.router.navigate([`/admin/contact/delete/${this.orgId}/${this.objectId}`])
    }

    public onDialogClose(): void {
        
    }

    get organizationEmergencyContacts(): Contact[] {
        let list: Contact[] = [];
        if (this.contacts != null) {
            list = this.contacts.filter(c => this.isEmergencyPOC(c.agentType));
        }
        return list;
    }

    get statutoryAgentRequired(): boolean {
        let result: boolean = false;
        const requiredTypes: string[] = ["CO", "CORP", "LC", "LLC", "BIZTRUST", "REITRUST", "TR", "LP", "LLP"]
        if (this.organizationType != null) {
            result = requiredTypes.findIndex(t => t === this.organizationType) !== -1;
        }
        return result;
    }

    selectDeletionDialog(toDelete: Contact): void {
        if (this.isPrimaryContact(toDelete.agentType)) {
            this.managedDeleteDialog.message = this.managedDeleteDialogMessage.replace(/UserName/gi, `${toDelete.firstName} ${toDelete.lastName}`);
            this.managedDeleteDialog.message = this.managedDeleteDialog.message.replace(/AgentType/gi, "Primary Contact");
            this.managedDeleteDialog.toggleDialog(true);
            this.dialogService.updateDialog(this.managedDeleteDialog);
        } else if (this.isExecutiveAuthority(toDelete.agentType)) {
            this.managedDeleteDialog.message = this.managedDeleteDialogMessage.replace(/UserName/gi, `${toDelete.firstName} ${toDelete.lastName}`);
            this.managedDeleteDialog.message = this.managedDeleteDialog.message.replace(/AgentType/gi, "Executive Authority");
            this.managedDeleteDialog.toggleDialog(true);
            this.dialogService.updateDialog(this.managedDeleteDialog);
        } else if (this.isEmergencyPOC(toDelete.agentType) && this.organizationEmergencyContacts.length <= 1) {
            this.managedDeleteDialog.message = this.managedDeleteDialogMessage.replace(/UserName/gi, `${toDelete.firstName} ${toDelete.lastName}`);
            this.managedDeleteDialog.message = this.managedDeleteDialog.message.replace(/AgentType/gi, "Emergency Contact");
            this.managedDeleteDialog.toggleDialog(true);
            this.dialogService.updateDialog(this.managedDeleteDialog);
        } else if (this.isStatutoryAgent(toDelete.agentType) && this.statutoryAgentRequired) {
            this.managedDeleteDialog.message = this.managedDeleteDialogMessage.replace(/UserName/gi, `${toDelete.firstName} ${toDelete.lastName}`);
            this.managedDeleteDialog.message = this.managedDeleteDialog.message.replace(/AgentType/gi, "Statutory Agent");
            this.managedDeleteDialog.toggleDialog(true);
            this.dialogService.updateDialog(this.managedDeleteDialog);
        } else {
            this.confirmDialog.message = this.confirmDialogMessage.replace(/UserName/gi, `${toDelete.firstName} ${toDelete.lastName}`);
            this.confirmDialog.toggleDialog(true);
            this.dialogService.updateDialog(this.confirmDialog);
        }
    }

    startEditFlow(): void {
        this.router.navigate(['flow', 'create', 'oBvXPWFoBA3A7e2GBM']);
    }

    typeFormatter(component: any) {
        return (obj: any) => {
            let contactTypes: AgentType = obj.data.agentType;
            let types: AgentType[] = [];
            let displayStrings: string[] = [];

            for (let prop in AgentType)
                if (typeof AgentType[prop] === 'number' && contactTypes & (<any>AgentType[prop]))
                    types.push(<any>AgentType[prop]);

            types.forEach((x) => {
                let description: string = '';
                switch (x) {
                    case AgentType.AuthorizedAgent:
                        description += AgentTypeOptions.AuthorizedAgent.text;
                        break;
                    case AgentType.DesignatedRep:
                        description += AgentTypeOptions.DesignatedRepresentative.text;
                        break;
                    case AgentType.EmergencyPOC:
                        description += AgentTypeOptions.EmergencyPOC.text;
                        break;
                    case AgentType.ExecutiveAuthority:
                        description += AgentTypeOptions.ExecutiveAuthority.text;
                        break;
                    case AgentType.PrimaryContact:
                        description += AgentTypeOptions.PrimaryContact.text;
                        break;
                    case AgentType.StatutoryAgent:
                        description += AgentTypeOptions.StatutoryAgent.text;
                        break;
                    default:
                        description += AgentType[x];
                        break;
                }
                displayStrings.push(description);
            });

            //If we have no entries add in the empty flag value
            if (displayStrings.length == 0)
                displayStrings.push(AgentTypeOptions.None.text);

            return displayStrings.join(', ');
        }
    }
}
